import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronRight,
  Home,
  Lock,
  Plus,
  X,
} from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import type { PropertyRow, SubscriptionTier } from '@/db/types';
import { goTo } from '@/lib/nav';
import {
  HOME_CAP,
  getActiveProperty,
  getSubscriptionTier,
  listProperties,
  setActiveProperty,
} from '@/lib/tasks';

/**
 * HomeSwitcher — the single surface for switching between properties and
 * adding a new one. Used in three places: desktop right-rail nickname
 * widget, mobile My Home tab title, and the header avatar menu.
 *
 * Layout:
 *   - Mobile: bottom sheet sliding up from the screen edge.
 *   - Desktop: centered popover card.
 * Both use the same backdrop-fade + dialog-translate animation as the
 * Emergency Card edit modal — backdrop fades 0→0.55, dialog rises 120px
 * → 0 in 280ms.
 *
 * Switching is instant: setActiveProperty writes the pointer, bumps the
 * data revision, and every property-scoped screen refetches. We close
 * the sheet after the write so the user lands on whatever tab they were
 * already on, now showing the new home's data.
 *
 * Add-home enforcement: the "+ Add a home" row is greyed at the tier
 * cap (1 for free, 5 for paid). Free tier users see an "Upgrade to
 * add more homes" affordance instead — that link is wired up once the
 * subscription surface exists; for now it's a placeholder.
 */

interface Props {
  visible: boolean;
  onClose: () => void;
  /** Optional headline override. Used after a home delete to prompt
   *  "Pick which home you want to enter now." instead of the default
   *  "Switch home" title — the post-delete picker is a one-time
   *  decision, not a casual switch. */
  headline?: string;
  /** Optional subhead under the headline. Same use case — give the
   *  user context about why this picker is appearing. */
  subhead?: string;
  /** When true, hide the "+ Add a home" row. We're forcing a choice
   *  among existing homes; adding a new one would loop the user back
   *  into onboarding mid-delete which is jarring. */
  hideAddRow?: boolean;
}

export function HomeSwitcher({ visible, onClose, headline, subhead, hideAddRow }: Props) {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const db = useSQLiteContext();
  const isDesktop = useIsDesktop();
  const [homes, setHomes] = useState<PropertyRow[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tier, setTier] = useState<SubscriptionTier>('paid');
  const [switching, setSwitching] = useState(false);

  // Animation: backdrop fades, dialog translates up from below.
  const anim = useSharedValue(0);
  const backdropStyle = useAnimatedStyle(() => ({
    opacity: anim.value * 0.55,
  }));
  const dialogStyle = useAnimatedStyle(() => ({
    opacity: 0.3 + anim.value * 0.7,
    transform: [{ translateY: (1 - anim.value) * 120 }],
  }));

  // Load homes whenever the sheet opens. Cheap query — listProperties +
  // getActiveProperty are both single-row reads against an indexed table.
  const refresh = useCallback(async () => {
    const [list, active, t] = await Promise.all([
      listProperties(db),
      getActiveProperty(db),
      getSubscriptionTier(db),
    ]);
    setHomes(list);
    setActiveId(active?.id ?? null);
    setTier(t);
  }, [db]);

  useEffect(() => {
    if (visible) {
      void refresh();
      anim.value = withTiming(1, {
        duration: 280,
        easing: Easing.out(Easing.cubic),
      });
    }
  }, [visible, refresh, anim]);

  /** Drive the exit animation, THEN close — preserves the slide-down
   *  feel instead of snapping the modal shut. */
  const closeWithAnim = useCallback(() => {
    anim.value = withTiming(
      0,
      { duration: 220, easing: Easing.in(Easing.cubic) },
      (finished) => {
        if (finished) runOnJS(onClose)();
      }
    );
  }, [anim, onClose]);

  const onPick = useCallback(
    async (id: string) => {
      if (switching || id === activeId) {
        // Tapping the current home just closes — feels right (no-op vs
        // error message), and avoids needless data-revision bumps.
        closeWithAnim();
        return;
      }
      setSwitching(true);
      try {
        await setActiveProperty(db, id);
      } finally {
        setSwitching(false);
      }
      closeWithAnim();
      // Land on the picked home's My Home tab (Wave 48). Switching used
      // to leave you wherever you were — picking a home reads as "take
      // me THERE," so honor it. Deferred past the close animation so
      // the sheet doesn't pop mid-navigation.
      setTimeout(() => goTo(router, '/(tabs)/my-home'), 240);
    },
    [db, switching, activeId, closeWithAnim, router]
  );

  const cap = HOME_CAP[tier];
  const atCap = homes.length >= cap;
  // Free tier with 1 home → the "+ Add a home" affordance turns into an
  // upgrade prompt. Paid tier at 5 → "Maximum reached" copy.
  const showUpgrade = tier === 'free' && atCap;

  const onAddHome = () => {
    if (atCap) return; // disabled; copy explains why
    closeWithAnim();
    // Defer the navigation to the next frame so the modal exit
    // animation gets a clean unmount before the new route pushes.
    setTimeout(() => goTo(router, '/add-home'), 220);
  };

  return (
    <Modal
      visible={visible}
      animationType="none"
      transparent
      onRequestClose={closeWithAnim}>
      <Animated.View style={[styles.backdrop, backdropStyle]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={closeWithAnim} />
      </Animated.View>
      <View
        style={[
          styles.center,
          // On desktop center the dialog; on mobile pin it to the bottom
          // so it reads as a sheet rising from the tab bar.
          !isDesktop && styles.centerMobile,
          !isDesktop && { paddingBottom: 24 + insets.bottom },
        ]}
        pointerEvents="box-none">
        <Animated.View
          style={[
            styles.dialog,
            isDesktop ? styles.dialogDesktop : styles.dialogMobile,
            dialogStyle,
          ]}>
          <View style={styles.head}>
            <View style={{ flex: 1 }}>
              <Text style={styles.eyebrow}>YOUR HOMES</Text>
              <Text style={styles.title}>
                {headline ??
                  (homes.length === 1
                    ? '1 home'
                    : `${homes.length} of ${cap} homes`)}
              </Text>
              {subhead ? <Text style={styles.subhead}>{subhead}</Text> : null}
            </View>
            <Pressable
              onPress={closeWithAnim}
              hitSlop={10}
              accessibilityLabel="Close"
              style={({ pressed }) => [styles.closeBtn, pressed && { opacity: 0.8 }]}>
              <X size={18} color={Palette.ink} strokeWidth={2} />
            </Pressable>
          </View>

          <ScrollView style={{ maxHeight: 360 }}>
            {homes.map((h) => {
              const isActive = h.id === activeId;
              const subtitle = h.address?.trim() || h.zip || 'No address';
              return (
                <Pressable
                  key={h.id}
                  onPress={() => onPick(h.id)}
                  disabled={switching}
                  style={({ pressed }) => [
                    styles.row,
                    isActive && styles.rowActive,
                    pressed && { opacity: 0.85 },
                  ]}>
                  <View
                    style={[
                      styles.rowIcon,
                      isActive && styles.rowIconActive,
                    ]}>
                    <Home
                      size={18}
                      color={isActive ? Palette.cream : Palette.forest}
                      strokeWidth={1.8}
                    />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.rowName} numberOfLines={1}>
                      {h.name?.trim() || 'Untitled home'}
                    </Text>
                    <Text style={styles.rowAddr} numberOfLines={1}>
                      {subtitle}
                    </Text>
                  </View>
                  {isActive ? (
                    <View style={styles.activeBadge}>
                      <Check size={13} color={Palette.cream} strokeWidth={2.4} />
                    </View>
                  ) : (
                    <ChevronRight
                      size={14}
                      color={Palette.inkFaint}
                      strokeWidth={1.8}
                    />
                  )}
                </Pressable>
              );
            })}
          </ScrollView>

          {/*
            Add / Upgrade row sits OUTSIDE the scroll so it's always
            in reach even with 5 homes. Free-tier upgrade replaces the
            add row so the user always sees the path forward.

            hideAddRow=true: post-delete picker mode. We're forcing
            the user to choose AMONG existing homes; adding a new one
            mid-delete loops them back into onboarding, which is
            confusing. The row gets dropped entirely.
          */}
          {hideAddRow ? null : showUpgrade ? (
            <Pressable
              onPress={closeWithAnim}
              style={({ pressed }) => [
                styles.addRow,
                styles.addRowUpgrade,
                pressed && { opacity: 0.9 },
              ]}>
              <View style={styles.upgradeIcon}>
                <Lock size={14} color={Palette.cream} strokeWidth={2} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.addLabel}>Upgrade to add more homes</Text>
                <Text style={styles.addSub}>
                  Free includes 1 home · Paid adds 4 more for vacation, family, rentals
                </Text>
              </View>
            </Pressable>
          ) : (
            <Pressable
              onPress={onAddHome}
              disabled={atCap}
              style={({ pressed }) => [
                styles.addRow,
                pressed && { opacity: 0.9 },
                atCap && { opacity: 0.45 },
              ]}>
              <View style={styles.addIcon}>
                <Plus size={16} color={Palette.cream} strokeWidth={2.4} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.addLabel}>
                  {atCap ? `Maximum ${cap} homes reached` : 'Add a home'}
                </Text>
                <Text style={styles.addSub}>
                  {atCap
                    ? 'Remove one to add another'
                    : 'Quick setup — about 30 seconds'}
                </Text>
              </View>
            </Pressable>
          )}
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#000',
  },
  center: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.four,
  },
  centerMobile: {
    justifyContent: 'flex-end',
    paddingBottom: 24,
  },
  dialog: {
    width: '100%',
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    elevation: 12,
  },
  dialogDesktop: {
    maxWidth: 420,
  },
  dialogMobile: {
    // Mobile sheet keeps a tiny rounded edge top-down so it reads as a
    // sheet rather than a floating dialog.
    maxWidth: 480,
    alignSelf: 'center',
  },

  head: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  eyebrow: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.honeyDeep,
    letterSpacing: 0.7,
  },
  title: {
    marginTop: 2,
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  subhead: {
    marginTop: 4,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 16,
    borderWidth: 1, borderColor: Palette.line,
    backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center',
  },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 10,
    paddingVertical: 11,
    borderRadius: Radius.lg,
    marginBottom: 6,
  },
  // Active home gets a soft forest-tinted wash + a forest icon so the
  // selection is unmistakable without competing with the row text.
  rowActive: {
    backgroundColor: '#EAF0EA',
    borderWidth: 1,
    borderColor: Palette.forest,
  },
  rowIcon: {
    width: 36, height: 36, borderRadius: 12,
    backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center',
  },
  rowIconActive: {
    backgroundColor: Palette.forest,
  },
  rowName: {
    fontFamily: Fonts.sansBold,
    fontSize: 14.5,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  rowAddr: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  activeBadge: {
    width: 22, height: 22, borderRadius: 11,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },

  addRow: {
    marginTop: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 10,
    paddingVertical: 12,
    borderRadius: Radius.lg,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: Palette.line,
  },
  addRowUpgrade: {
    backgroundColor: 'rgba(225,163,61,0.10)',
    borderColor: Palette.honeyDeep,
    borderStyle: 'solid',
  },
  addIcon: {
    width: 32, height: 32, borderRadius: 10,
    backgroundColor: Palette.forest,
    alignItems: 'center', justifyContent: 'center',
  },
  upgradeIcon: {
    width: 32, height: 32, borderRadius: 10,
    backgroundColor: Palette.honeyDeep,
    alignItems: 'center', justifyContent: 'center',
  },
  addLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  addSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
  },
});
